<template>
  <standard-dialog
    ref="dialogRef"
    v-model="model"
    :title="$t('makeOrder') + ' #' + (order ? order.id : '')"
    icon="mdi-receipt"
    max-width="550"
    hide-actions
  >
    <template #header-actions>
      <circle-button
        icon="mdi-printer"
        color="black"
        :tooltip="$t('print')"
        @click="printOrder"
      />
    </template>

    <div v-if="order" ref="scaleOuter" class="order-details" :style="scaleOuterStyle">
      <div ref="scaleInner" class="order-details-scale-inner" :style="scaleInnerStyle">
        <!-- Status Buttons - 4 per row -->
        <div class="status-buttons-grid mb-3">
          <v-btn
            v-for="status in orderStatuses"
            :key="status.value"
            :color="currentState === status.value ? getStatusColor(status.value) : 'white'"
            :dark="currentState === status.value"
            small
            depressed
            class="status-btn"
            :class="{ 'status-btn-inactive': currentState !== status.value }"
            :loading="changingStatusOrderId === order.id && currentState !== status.value"
            @click="handleStatusClick(status.value)"
          >
            <v-icon x-small left>{{ status.icon }}</v-icon>
            {{ $t(status.value) }}
          </v-btn>
        </div>

        <!-- Guest Card -->
        <v-card outlined class="mb-3 guest-card">
          <v-card-text class="pa-3">
            <div class="d-flex align-center">
              <v-avatar color="orange lighten-4" size="40" class="mr-3">
                <v-icon color="orange darken-2">mdi-account</v-icon>
              </v-avatar>
              <div class="flex-grow-1">
                <div class="font-weight-bold text-body-1">
                  {{ guest ? toLatin(guest.name || "") : "" }} {{ guest ? toLatin(guest.lastname || "") : "" }}
                </div>
                <a
                  v-if="guest && guest.phone"
                  :href="'tel:' + guest.phone"
                  class="text-body-2 font-weight-bold orange--text text--darken-3 d-flex align-center phone-link"
                  @click.stop
                >
                  <v-icon small color="orange darken-3" class="mr-1">mdi-phone</v-icon>
                  {{ guest.phone }}
                </a>
              </div>
              <!-- Delivery Type Badge -->
              <v-chip small outlined color="orange">
                <v-icon x-small left color="orange">
                  {{
                    order.delivery_type === 0
                      ? "mdi-motorbike"
                      : order.delivery_type === 1
                      ? "mdi-silverware-fork-knife"
                      : "mdi-home"
                  }}
                </v-icon>
                {{
                  order.delivery_type === 0
                    ? $t("delivery_to_address")
                    : order.delivery_type === 1
                    ? $t("pickup")
                    : $t("consumption_in_restaurant")
                }}
              </v-chip>
            </div>
          </v-card-text>
        </v-card>

        <!-- Order Items -->
        <div class="order-items mb-3">
          <div class="d-flex align-center mb-2">
            <v-icon small color="orange" class="mr-2">mdi-food</v-icon>
            <span class="font-weight-medium">{{ $t("ordered") }}</span>
          </div>
          <div
            v-for="(transaction, idx) in orderLines"
            :key="transaction.id || idx"
            class="order-item d-flex justify-space-between align-center py-2"
            :class="{
              'border-bottom': idx < orderLines.length - 1,
              'order-item--choice': transaction.line_type === 'choice',
              'order-item--package': transaction.line_type === 'package',
            }"
          >
            <div class="d-flex align-center item-name">
              <v-chip
                v-if="transaction.line_type !== 'choice'"
                x-small
                color="orange lighten-4"
                text-color="orange darken-2"
                class="mr-2 font-weight-bold qty-chip"
              >
                {{ transaction.quantity }}x
              </v-chip>
              <v-icon v-else x-small class="mr-2" color="grey">mdi-subdirectory-arrow-right</v-icon>
              <span
                class="text-body-2"
                :class="{ 'font-weight-bold': transaction.line_type === 'package' }"
              >
                <template v-if="transaction.line_type === 'choice' && transaction.quantity > 1">
                  {{ transaction.quantity }}x
                </template>
                {{ toLatin(transaction?.food?.name || transaction?.meals?.name || "") }}
              </span>
            </div>
            <span
              v-if="transaction.line_type !== 'choice'"
              class="text-body-2 font-weight-medium item-price"
            >
              {{ (transaction.price * transaction.quantity).toFixed(2) }}
              {{ orderCurrency }}
            </span>
            <span
              v-else-if="Number(transaction.price) > 0"
              class="text-body-2 grey--text text--darken-1 item-price"
            >
              +{{ (transaction.price * transaction.quantity).toFixed(2) }}
              {{ orderCurrency }}
            </span>
          </div>
        </div>

        <!-- Delivery Info with Time -->
        <v-card outlined class="mb-3" v-if="order.delivery_type === 0">
          <v-card-text class="pa-3">
            <div class="d-flex">
              <!-- Left side - Address -->
              <div class="flex-grow-1" style="flex: 1">
                <div class="d-flex align-center mb-2">
                  <v-icon small color="orange" class="mr-2">mdi-map-marker</v-icon>
                  <span class="font-weight-medium">{{ $t("delivery_address") }}</span>
                </div>
                <div class="pl-6">
                  <div v-if="order.location">
                    <div class="text-body-2">
                      {{ order.location.address }}
                    </div>
                    <div class="text-caption grey--text">
                      {{ order.location.city ? order.location.city.name : "" }}
                      <span v-if="order.location.apartment">
                        • {{ $t("apartment") }}: {{ order.location.apartment }}
                      </span>
                      <span v-if="order.location.floor">
                        • {{ $t("floor") }}: {{ order.location.floor }}
                      </span>
                    </div>
                  </div>
                  <div v-else class="text-body-2 grey--text">-</div>
                </div>
              </div>
              <!-- Right side - Time Info -->
              <div
                class="d-flex flex-column"
                style="flex: 1; border-left: 1px solid #e0e0e0; padding-left: 16px"
              >
                <div class="text-center mb-2">
                  <div class="text-caption grey--text text-uppercase">
                    {{ $t("ordered") }}
                  </div>
                  <div class="text-body-2 font-weight-medium">
                    {{ formatDateTime(order.created_at) }}
                  </div>
                </div>
                <v-divider class="my-1"></v-divider>
                <div class="text-center mt-2">
                  <div class="text-caption grey--text text-uppercase">
                    {{ $t("deliver_until") }}
                  </div>
                  <div class="text-body-2 font-weight-bold orange--text">
                    {{ formatScheduledDate(order.date) }}
                    {{ order.delivery_time ? order.delivery_time.substring(0, 5) : "" }}
                  </div>
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>

        <!-- Time Info Card (for non-delivery orders) -->
        <v-card outlined class="mb-3" v-else>
          <v-card-text class="pa-3">
            <div class="d-flex justify-space-between">
              <div class="text-center flex-grow-1">
                <div class="text-caption grey--text text-uppercase">
                  {{ $t("ordered") }}
                </div>
                <div class="text-body-2 font-weight-medium">
                  {{ formatDateTime(order.created_at) }}
                </div>
              </div>
              <v-divider vertical class="mx-2"></v-divider>
              <div class="text-center flex-grow-1">
                <div class="text-caption grey--text text-uppercase">
                  {{ $t("deliver_until") }}
                </div>
                <div class="text-body-2 font-weight-bold orange--text">
                  {{ formatScheduledDate(order.date) }}
                  {{ order.delivery_time ? order.delivery_time.substring(0, 5) : "" }}
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>

        <!-- Total -->
        <v-card color="orange lighten-5" flat>
          <v-card-text class="pa-3">
            <div class="d-flex justify-space-between align-center">
              <span class="text-subtitle-1 font-weight-bold">{{ $t("total") }}</span>
              <span class="text-h6 font-weight-bold orange--text text--darken-2">
                {{ calculateOrderTotal.toFixed(2) }} {{ orderCurrency }}
              </span>
            </div>
          </v-card-text>
        </v-card>
      </div>
    </div>

    <prep-time-dialog
      v-model="prepTimeDialog"
      @select-minutes="confirmPrepMinutes"
      @ready-now="confirmReadyNow"
    />
  </standard-dialog>
</template>

<script>
import hasModel from "@/mixins/hasModel";
import hasPrinting from "@/mixins/hasPrinting";
import StandardDialog from "@/components/dialogs/StandardDialog.vue";
import PrepTimeDialog from "@/components/guest/PrepTimeDialog.vue";
import { choiceQuantity, orderTotal } from "@/utils/guestOrderTotal";
import reportService from "@/services/report";

export default {
  name: "GuestOrderDetailsDialog",
  components: {
    StandardDialog,
    PrepTimeDialog,
  },
  mixins: [hasModel, hasPrinting],
  props: {
    order: {
      type: Object,
      default: null,
    },
    guest: {
      type: Object,
      default: null,
    },
    changingStatusOrderId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      orderStatuses: [
        { value: "pending", icon: "mdi-clock-outline" },
        { value: "accepted", icon: "mdi-check" },
        { value: "in-progress", icon: "mdi-progress-clock" },
        { value: "ready", icon: "mdi-food" },
        { value: "on-hold", icon: "mdi-pause" },
        { value: "delivered", icon: "mdi-truck-check" },
        { value: "completed", icon: "mdi-check-all" },
        { value: "cancelled", icon: "mdi-cancel" },
        { value: "rejected", icon: "mdi-close-circle-outline" },
      ],
      contentScale: 1,
      naturalContentHeight: 0,
      minContentScale: 0.55,
      prepTimeDialog: false,
    };
  },
  computed: {
    scaleOuterStyle() {
      if (this.contentScale >= 1 || !this.naturalContentHeight) return {};
      return {
        height: Math.ceil(this.naturalContentHeight * this.contentScale) + "px",
        overflow: "hidden",
      };
    },
    scaleInnerStyle() {
      if (this.contentScale >= 1) return {};
      return {
        transform: `scale(${this.contentScale})`,
        transformOrigin: "top left",
        width: 100 / this.contentScale + "%",
      };
    },
    /**
     * Stavke narudžbe u ravnoj listi: paket (ili obično jelo), pa ispod njega izbori iz
     * `prilog[]` (deca sa parent_id). `line_type` sa servera ima prednost kad postoji.
     */
    orderLines() {
      const lines = [];
      (this.order?.order_guest_transactions || []).forEach((transaction) => {
        const isPackage = transaction.line_type
          ? transaction.line_type === "package"
          : Boolean(transaction.food?.is_package);
        lines.push({ ...transaction, line_type: isPackage ? "package" : transaction.line_type });
        // Gost: quantity izbora je već ukupna; radnik (line_type): quantity × količina paketa.
        (transaction.prilog || []).forEach((choice) => {
          lines.push({
            ...choice,
            line_type: "choice",
            quantity: choiceQuantity(transaction, choice) || 1,
          });
        });
      });
      return lines;
    },
    /** Backend za otkazanu narudžbu vraća "support-canceled"; dugme „Otkazano” šalje "cancelled". */
    currentState() {
      return this.order?.state === "support-canceled" ? "cancelled" : this.order?.state;
    },
    calculateOrderTotal() {
      return orderTotal(this.order);
    },
    // Currency of the order's own restaurant, not the logged-in restaurant.
    orderCurrency() {
      return this.order?.restaurant?.settings?.price || this.restaurantCurrency;
    },
  },
  watch: {
    model(isOpen) {
      if (isOpen) {
        this.$nextTick(() => this.fitContentToScreen());
      }
    },
    order() {
      if (this.model) {
        this.$nextTick(() => this.fitContentToScreen());
      }
    },
  },
  mounted() {
    window.addEventListener("resize", this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
  },
  methods: {
    // Accepting an order asks for a prep time first, same as on the kitchen
    // display board - either "ready immediately" or "ready in X minutes".
    // Every other status change goes straight through.
    handleStatusClick(newStatus) {
      if (newStatus === "accepted") {
        this.prepTimeDialog = true;
        return;
      }
      this.$emit("change-status", { order: this.order, guest: this.guest, newStatus });
    },
    // minuti pripreme idu uz prihvatanje kao vreme spremnosti (vidi reportService.rememberPrepMinutes)
    confirmPrepMinutes(minutes) {
      reportService.rememberPrepMinutes(this.order.id, minutes);
      this.$emit("change-status", {
        order: this.order,
        guest: this.guest,
        newStatus: "accepted",
      });
      this.prepTimeDialog = false;
    },
    confirmReadyNow() {
      this.$emit("change-status", {
        order: this.order,
        guest: this.guest,
        newStatus: "ready",
      });
      this.prepTimeDialog = false;
    },
    handleResize() {
      if (this.model) {
        this.fitContentToScreen();
      }
    },
    getAvailableHeight() {
      const dialogEl = this.$refs.dialogRef && this.$refs.dialogRef.$el;
      if (!dialogEl || !dialogEl.querySelector) return null;
      const header = dialogEl.querySelector(".dialog-header");
      const headerHeight = header ? header.offsetHeight : 0;
      const bodyVerticalPadding = 48; // .dialog-body has 24px top + 24px bottom padding
      const dialogMargin = 64; // matches v-dialog max-height: calc(100vh - 64px)
      const safetyMargin = 16;
      return (
        window.innerHeight -
        dialogMargin -
        headerHeight -
        bodyVerticalPadding -
        safetyMargin
      );
    },
    fitContentToScreen() {
      // Reset scale before measuring the natural (unscaled) content height
      this.contentScale = 1;
      this.naturalContentHeight = 0;
      this.$nextTick(() => {
        const inner = this.$refs.scaleInner;
        if (!inner) return;
        const naturalHeight = inner.scrollHeight;
        const availableHeight = this.getAvailableHeight();
        if (!availableHeight || naturalHeight <= availableHeight) {
          return;
        }
        const scale = Math.max(this.minContentScale, availableHeight / naturalHeight);
        this.naturalContentHeight = naturalHeight;
        this.contentScale = scale;
      });
    },
    formatDate(date) {
      if (!date) return "";
      return new Date(date).toLocaleDateString("sr-Latn", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    },
    formatDateTime(dateTime) {
      if (!dateTime) return "";
      return new Date(dateTime).toLocaleString("sr-Latn", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    isSameDayAsOrder(date) {
      if (!date || !this.order || !this.order.created_at) return false;
      const scheduled = new Date(date);
      const ordered = new Date(this.order.created_at);
      return (
        scheduled.getFullYear() === ordered.getFullYear() &&
        scheduled.getMonth() === ordered.getMonth() &&
        scheduled.getDate() === ordered.getDate()
      );
    },
    formatScheduledDate(date) {
      if (!date || this.isSameDayAsOrder(date)) return "";
      return this.formatDate(date);
    },
    getStatusColor(status) {
      const colors = {
        pending: "orange",
        accepted: "blue",
        "in-progress": "cyan",
        ready: "purple",
        "on-hold": "grey",
        delivered: "green",
        completed: "green",
        cancelled: "red",
        "support-canceled": "red",
        rejected: "red",
        deleted: "red",
      };
      return colors[status] || "grey";
    },
    getStatusText(status) {
      return status ? status.replace("-", " ") : "";
    },
    printOrder() {
      if (!this.order) return;

      const guestAndDeliveryInfo = `
        <div style="display: flex; justify-content: space-between; gap: 16px; margin-bottom: 16px;">
          <div style="background: #f9f9f9; padding: 16px; border-radius: 8px; flex: 1;">
            <div style="font-size: 18px; font-weight: bold; margin-bottom: 8px;">
              ${this.guest ? this.toLatin(this.guest.name || "") : ""} ${
        this.guest ? this.toLatin(this.guest.lastname || "") : ""
      }
            </div>
            <div style="font-size: 14px; color: #666; margin-bottom: 4px;">
              <strong>${this.$t("phone")}:</strong> ${this.guest ? this.guest.phone : ""}
            </div>
            <div style="font-size: 14px;">
              <strong>${this.$t("status")}:</strong> 
              <span style="font-weight: bold;">
                ${this.$t(this.order.state)}
              </span>
            </div>
          </div>
          <div style="background: #f9f9f9; padding: 16px; border-radius: 8px;display: flex; flex: 1;justify-content: flex-end;">
            <div>
              <div style="font-size: 16px; font-weight: bold; margin-bottom: 8px;">
              ${this.$t("delivery_address")}
            </div>
            ${
              this.order.location
                ? `
              <div style="font-size: 14px; margin-bottom: 4px;">
                ${this.order.location.address}
              </div>
              <div style="font-size: 14px; color: #666;">
                ${this.order.location.city ? this.order.location.city.name : ""}
                ${
                  this.order.location.apartment
                    ? "• " + this.$t("apartment") + ": " + this.order.location.apartment
                    : ""
                }
                ${
                  this.order.location.floor
                    ? "• " + this.$t("floor") + ": " + this.order.location.floor
                    : ""
                }
              </div>
            `
                : `<div style="font-size: 14px; color: #666;">-</div>`
            }
          </div>
        </div>
        </div>
      `;

      const timeInfo = `
        <div style="display: flex; justify-content: space-between; background: #f9f9f9; padding: 16px; border-radius: 8px; margin-bottom: 16px;">
          <div>
            <div style="font-size: 12px; color: #666; text-transform: uppercase; margin-bottom: 4px;">
              ${this.$t("ordered")}
            </div>
            <div style="font-size: 14px; font-weight: bold;">
              ${this.formatDateTime(this.order.created_at)}
            </div>
          </div>
          <div style="text-align: right;">
            <div style="font-size: 12px; color: #666; text-transform: uppercase; margin-bottom: 4px;">
              ${this.$t("deliver_until")}
            </div>
            <div style="font-size: 14px; font-weight: bold;">
              ${this.formatScheduledDate(this.order.date)}
              ${this.order.delivery_time ? this.order.delivery_time.substring(0, 5) : ""}
            </div>
          </div>
        </div>
      `;

      const headers = [
        { text: this.$t("food"), value: "name", align: "left" },
        {
          text: this.$t("price") + " " + this.orderCurrency,
          value: "total",
          align: "right",
        },
      ];

      const items = this.orderLines.map((line) => {
        const name = line.food ? this.toLatin(line.food.name) : "";
        const isChoice = line.line_type === "choice";
        const price = (line.price || 0) * line.quantity;
        return {
          name: isChoice ? "   – " + name : line.quantity + "x " + name,
          total: isChoice && !price ? "" : (isChoice ? "+" : "") + price.toFixed(2),
        };
      });

      const totalHtml = `
        <div style="background: #fff3e0; padding: 16px; border-radius: 8px; margin-top: 16px;">
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <span style="font-size: 18px; font-weight: bold;">${this.$t("total")}</span>
            <span style="font-size: 22px; font-weight: bold;">
              ${this.calculateOrderTotal.toFixed(2)} ${this.orderCurrency}
            </span>
          </div>
        </div>
      `;

      this.printDocument({
        pageTitle: this.$t("makeOrder") + " #" + this.order.id,
        title: this.$t("makeOrder") + " #" + this.order.id,
        beforeTableHtml: guestAndDeliveryInfo + timeInfo,
        hideDatetime: true,
        tables: [
          {
            headers: headers,
            items: items,
          },
        ],
        afterTableHtml: totalHtml,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.order-item--choice {
  padding-left: 28px !important;
}

.order-details {
  min-height: 200px;
}

.order-details-scale-inner {
  transition: transform 0.15s ease-out;
}

.status-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 8px;
  background: #f5f5f5;
  border-radius: 8px;
}

.guest-card {
  border-radius: 8px !important;
  border-color: #e0e0e0 !important;
  background: linear-gradient(135deg, #fff9f5 0%, #ffffff 100%);
}

.status-buttons-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
}

.status-btn {
  text-transform: none !important;
  font-size: 11px !important;
  letter-spacing: 0 !important;
  width: 100% !important;
  padding: 0 4px !important;
}

.status-btn-inactive {
  border: 1px solid #e0e0e0 !important;
  color: #666 !important;
}

.status-btn-inactive:hover {
  background-color: #f5f5f5 !important;
}

.order-items {
  background: #fafafa;
  border-radius: 8px;
  padding: 8px 12px;
}

.order-item {
  gap: 8px;
}

.order-item .item-name {
  flex: 1;
  min-width: 0;
}

.order-item .item-name .text-body-2 {
  word-break: break-word;
}

.order-item .item-price {
  min-width: 80px;
  text-align: right;
  flex-shrink: 0;
}

.order-item .qty-chip {
  flex-shrink: 0;
}

.order-item.border-bottom {
  border-bottom: 1px dashed #e0e0e0;
}

.cursor-pointer {
  cursor: pointer;
}

.phone-link {
  text-decoration: none;
  width: fit-content;
}

.phone-link:hover {
  text-decoration: underline;
}
</style>
